import {Observable} from 'rxjs';
import {APIResponse, Filter, FilterFieldOption, FilterType} from '@core/interfaces/system/system-common';

export interface GenericPopoutSettings {
    filters: GenericPopoutFilter[];
}

export interface GenericPopoutFilter extends Filter {
    order?: number; // optional order in list of filters
    webFilterType?: FilterType; // specifies a web filter type
    // FilterFieldType.STRING
    options?: FilterFieldOption[];
    // FilterFieldType.NUMERIC
    min?: number;
    max?: number;
}

export interface AnalyzerPopoutSettings extends GenericPopoutSettings {
    filters: AnalyzerPopoutFilter[];
    groups: AnalyzerPopoutGroup[];
    sensitivity: AnalyzerPopoutSensitivity[];
    inputs: AnalyzerPopoutInput[];
}

export interface AnalyzerPopoutFilter extends GenericPopoutFilter {}

export interface AnalyzerPopoutInput {
    fieldKey: string;
    fieldName: string;
    description: string;
    suffix: string;
    min: number;
    max: number;
    value: number;
}

export interface AnalyzerPopoutGroup {
    fieldKey: string;
    fieldName: string;
    selected: boolean;
}

export interface AnalyzerPopoutSensitivity {
    alias?: string; // e.g. alias for workflow ("general_study")
    fieldKey?: number; // e.g. workflowItemId
    fieldName: string;
    selected: boolean;
}

export interface GeospatialFilterSettings extends GenericPopoutSettings {}

// Abstract settings for popout with dropdowns and sliders
export interface SliderSettingsBase {
    id: string;
    description: string;
    prefix: string;
    suffix: string;
    max: number;
    min: number;
    value: number;
    values: number[];
}

export interface DropdownSettingsBase {
    id: string;
    description: string;
    dirtyYears: number[];
    sliders: SliderSettingsBase[];
}

export interface PopoutSettingsBase {
    customPeriodType: boolean;
    years: number[];
    dropdowns: DropdownSettingsBase[];
}

// Settings for Optimizer popout with dropdowns and sliders
export interface OptimizerSliderSettings extends SliderSettingsBase {
    backlog: number;
}

export interface OptimizerDropdownSettings extends DropdownSettingsBase {
    sliders: OptimizerSliderSettings[];
    backlog: number[];
}

export interface OptimizerPopoutSettings extends PopoutSettingsBase {
    constrained: boolean;
    dropdowns: OptimizerDropdownSettings[];
}

// Settings for Forecaster popout with dropdowns and sliders
export interface ForecasterSliderSettings extends SliderSettingsBase {
    effectiveness: number;
}

export interface ForecasterDropdownSettings extends DropdownSettingsBase {
    sliders: ForecasterSliderSettings[];
}

export interface ForecasterPopoutSettings extends PopoutSettingsBase {
    dropdowns: ForecasterDropdownSettings[];
}

// Data audit popout
export interface DataAuditPopoutSettings {
    filters: {
        selected: boolean;
        code: string;
        name: string;
        enabled: boolean;
        options: {
            code: string;
            name: string;
            enabled: boolean;
            selected: true;
        }[];
        stringFlag: boolean;
    }[];
}

export abstract class PopoutService {
    abstract getAnalyzerPopoutSettings(): Observable<AnalyzerPopoutSettings>;

    abstract getFilterOptions(workflowItemId, physicalField, search?, filters?): Observable<FilterFieldOption[]>;

    abstract getOptimizerPopoutSettings(
        studyId: number,
        panelConfig: number,
    ): Observable<APIResponse<OptimizerPopoutSettings>>;

    abstract getForecasterPopoutSettings(): Observable<ForecasterPopoutSettings>;

    abstract getDataAuditToolPopoutSettings(): Observable<DataAuditPopoutSettings>;
}
